import { useMemo } from 'react'

import { useAllAssessmentRequirementsQuery } from 'queries/company'

/**
 * Worker requirement IDs keyed by display label `{positionTemplateName} Level {n}`.
 * Each list is cumulative: a requirement defined at level L is included under that
 * template’s Level L, Level L+1, … through the highest level defined for that template.
 *
 * @example
 * Given the following assessment level requirements:
 * ```
 * [
 *   {
 *     positionTemplateName: 'Line Cook',
 *     level: 1,
 *     workerRequirementId: 101,
 *   },
 *   {
 *     positionTemplateName: 'Line Cook',
 *     level: 2,
 *     workerRequirementId: 103,
 *   },
 *   {
 *     positionTemplateName: 'Server',
 *     level: 1,
 *     workerRequirementId: 201,
 *   },
 * ],
 * ```
 * Return the following:
 * ```
 * {
 *   'Line Cook Level 1': [101],
 *   'Line Cook Level 2': [101, 103],
 *   'Server Level 1': [201],
 * }
 * ```
 */
export type AssessmentRequirementsByTemplateAndLevel = Record<string, number[]>

function levelLabel(positionTemplateName: string, level: number) {
  return `${positionTemplateName} Level ${level}`
}

/**
 * Loads assessment requirements and groups cumulative IDs by template + level label.
 *
 * @example
 * const { data, isPending } = useAssessmentLevelRequirements()
 * const ids = data['Server Level 1'] ?? []
 */
export const useAssessmentLevelRequirements = () => {
  const { data: requirements = [], isPending } =
    useAllAssessmentRequirementsQuery()

  const data = useMemo(() => {
    /** Track the highest level defined for each template
     * so that lower level requirements are included in higher level requirements
     */
    const maxLevelByTemplate = requirements.reduce<Map<string, number>>(
      (currentMax, req) => {
        const { positionTemplateName, level } = req
        currentMax.set(
          positionTemplateName,
          Math.max(currentMax.get(positionTemplateName) ?? 0, level)
        )
        return currentMax
      },
      new Map()
    )

    return requirements.reduce<AssessmentRequirementsByTemplateAndLevel>(
      (requirementsByTemplate, req) => {
        const { positionTemplateName, level, workerRequirementId } = req
        const maxLevel = maxLevelByTemplate.get(positionTemplateName) ?? level
        for (let i = level; i <= maxLevel; i++) {
          const key = levelLabel(positionTemplateName, i)
          requirementsByTemplate[key] ??= []
          requirementsByTemplate[key].push(workerRequirementId)
        }
        return requirementsByTemplate
      },
      {}
    )
  }, [requirements])

  return { data, isPending }
}
